import classnames from 'classnames';
import isString from 'lodash/isString';
import { ProgressSpinner } from 'primereact/progressspinner';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { SheetType } from '../data/print';

export default class Thumbnail extends Component {
	constructor(props) {
		super(props);

		this.state = {
			isFetching: false,
			image: null,
		};
	}

	componentDidMount() {
		const { image } = this.props;

		this.setImage(image);
	}

	componentDidUpdate(prevProps) {
		const { image } = this.props;

		if (prevProps.image !== image) {
			this.setImage(image);
		}
	}

	/**
	 * Return thumbnail image css selector.
	 *
	 * For example,
	 * - shipping-label-dynamic
	 * - shipping-label-static
	 * - netstamps-tradition-static
	 * - netstamps-promo-paper-20-static
	 * - cm-3610-dynamic
	 *
	 * @returns {string}
	 */
	getThumbnailImageCls = () => {
		const { state, sheet } = this.props;
		const { image } = this.state;

		// 2 is selected state, which roll type does not have
		if (
			(sheet === SheetType.ShippingLabel && state === 2) ||
			sheet === SheetType.Roll
		) {
			return classnames('thumbnail-image', {
				'shipping-label-dynamic': isString(image),
				'shipping-label-static': !isString(image),
			});
		} else if (sheet === SheetType.NetStamps) {
			return 'thumbnail-image netstamps-static';
		} else if (sheet === SheetType.Envelope) {
			return classnames('thumbnail-image', {
				'envelope-dynamic': isString(image),
				'envelope-static': !isString(image),
			});
		}

		return 'thumbnail-image';
	};

	/**
	 * Store the image URL in the state object.
	 */
	setImage = image => {
		this.setState({
			image,
			isFetching: isString(image),
		});
	};

	// Click event handler
	onClick = event => {
		const { position, onSelect } = this.props;

		event.preventDefault();

		if (onSelect) {
			onSelect({ position });
		}
	};

	// Load event handler. Show the image after the image is loaded in DOM.
	onImageLoad = () => {
		this.setState({
			isFetching: false,
		});
	};

	// Error event handler. Show the static image instead if fetching the image fails.
	onImageError = () => {
		this.setState({
			isFetching: false,
			image: null,
		});
	};

	/**
	 * Render image DOM object. If the thumbnail image is not available or fetch fails,
	 * it renders a div element with a static image instead.
	 */
	renderLabelImage() {
		const { width, height } = this.props;
		const { isFetching, image } = this.state;

		const thumbnailImageCls = this.getThumbnailImageCls();

		if (isString(image)) {
			return (
				<>
					<img
						src={image}
						className={thumbnailImageCls}
						onLoad={this.onImageLoad}
						onError={this.onImageError}
						style={{
							width,
							height,
							display: isFetching ? 'none' : 'block',
						}}
						alt="shipping-label"
					/>
					{isFetching && (
						<ProgressSpinner strokeWidth="2" animationDuration=".7s" />
					)}
				</>
			);
		}

		return (
			<div
				className={thumbnailImageCls}
				style={{
					width,
					height,
				}}
			/>
		);
	}

	render() {
		const { selectable, state, width, height, sheet, series } = this.props;

		const cls = classnames('common-print-thumbnail', {
			[`thumbnail-${sheet}`]: true,
			[`thumbnail-${series}`]: true,

			selectable,
			'un-selectable': !selectable,

			// one of additional selector if thumbnail is selectable
			available: selectable && state === 0,
			printed: selectable && state === 1,
			selected: selectable && state === 2,
		});

		if (selectable) {
			return (
				<a
					href="/"
					className={cls}
					style={{
						width,
						height,
					}}
					tabIndex={0}
					role="button"
					onClick={this.onClick}
				>
					{this.renderLabelImage()}
				</a>
			);
		} else {
			return (
				<div
					className={cls}
					style={{
						width,
						height,
					}}
				>
					{this.renderLabelImage()}
				</div>
			);
		}
	}
}

Thumbnail.propTypes = {
	// sheet
	sheet: PropTypes.string,

	// series
	series: PropTypes.string,

	// thumbnail's width
	width: PropTypes.number,

	// thumbnail's height
	height: PropTypes.number,

	// thumbnail's dynamic image
	image: PropTypes.string,

	// `true` if the thumbnail is selectable
	selectable: PropTypes.bool,

	// Event handler for thumbnail selection
	onSelect: PropTypes.func,
};
